<template>
  <VContainer>
    <div
      v-if="showFullLoader"
      class="full-page-loader"
    >
      <Preloader />
    </div>
    <div v-if="!showFullLoader">
      <VacancyStepTitle
        v-if="vacancy"
        :progress="20"
        :vacancy="vacancy"
        :step="1"
      >
        {{ $t('vacancyInformation') }}
      </VacancyStepTitle>
      <VacancyForm
        v-if="!vacancyId || !showFullLoader && vacancy"
        class="mt-6"
        :vacancy="vacancy"
        :user="user"
      >
        <template v-slot:buttons="{ submit }">
          <VBtn
            color="success"
            dark
            :loading="loading && !showFullLoader"
            elevation="0"
            @click="next(submit)"
          >
            {{ $t('next') }} &rarr;
          </VBtn>
        </template>
      </VacancyForm>
    </div>
  </VContainer>
</template>

<script>
import VacancyStepTitle from '@/views/Vacancy/VacancyStepTitle';
import VacancyForm from '../../components/Vacancy/VacancyForm.vue';
import ValidationMixin from '../../mixins/ValidationMixin';
import Vacancy from '../../orm/models/Vacancy/Vacancy';
import Preloader from '../../components/Preloader.vue';

export default {
  name: 'VacancyStepOne',
  metaInfo() {
    return {
      title: this.$t('vacancyInformation'),
    };
  },
  components: {
    VacancyForm,
    VacancyStepTitle,
    Preloader,
  },
  mixins: [ValidationMixin],
  props: {
    user: {
      type: Object,
      required: true,
    },
    company: {
      type: Object,
      required: true,
    },
    vacancyId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      showFullLoader: false,
    };
  },
  computed: {
    canStoreVacancies() {
      return this.company.permissions.vacancyStore;
    },
    vacancy() {
      if (!this.vacancyId) {
        return undefined;
      }
      return Vacancy.query().find(this.vacancyId);
    },
  },
  created() {
    if (this.vacancyId) {
      this.fetchData();
    }
  },
  methods: {
    async next(submitForm) {
      this.showFullLoader = true;
      this.loading = true;

      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      try {
        const vacancy = await submitForm();

        if (vacancy !== null && !this.vacancyId) {
          await this.$router.push({ name: 'vacancy-store', params: { id: vacancy.id } });
        }
        const { default: Wizard } = await import('@/orm/models/Wizard/Wizard');
        Wizard
          .setVacancyId(vacancy.id)
          .setIsCompleted(false)
          .setWizardStep(this.$enums?.wizardSteps?.AI_QUESTIONS || 'ai_questions');
        Wizard.setIsAiTemplate(true);
        await Wizard.saveWizardState();

        this.loading = false;

        window.scrollTo(0, scrollPosition);
        await this.$router.push({
          name: 'vacancy-ai-questions',
          params: { vacancyId: vacancy.id },
        });
      } catch (error) {
        this.loading = false;
        this.showFullLoader = false;
        throw error;
      }
    },
    checkCanStoreVacancy() {
      if (this.canStoreVacancies) {
        return;
      }

      this.$toast.error(this.$t('fillCompanyInfo'));
      this.$router.push({ name: 'dashboard' });
    },
    async fetchData() {
      await this.withApiErrorHandling(() => {
        Vacancy.api().fetchById(this.vacancyId);
      }, (e) => {
        this.$toast.error(e.message);
      }, 'showFullLoader');
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => vm.checkCanStoreVacancy());
  },
  beforeRouteUpdate(to, from, next) {
    this.checkCanStoreVacancy();
    next();
  },
};
</script>

<style scoped lang="scss">
.full-page-loader {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

<i18n locale="en" lang="yaml">
  next: Next
  fillCompanyInfo: You must fill the company information before posting a job
  vacancyInformation: Job description
</i18n>

<i18n locale="ru" lang="yaml">
  next: Далее
  fillCompanyInfo: Прежде чем разместить вакансию, необходимо заполнить информацию о компании
  vacancyInformation: Информация о вакансии
</i18n>
